import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Copy, Download, ExternalLink, Facebook, Instagram, Loader2, Share2, Twitch, Youtube,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fileUrl } from "@/lib/api";

const PLATFORMS = [
    { id: "youtube", label: "YouTube Studio", href: "https://www.youtube.com/upload", icon: Youtube, hint: "Upload as a Short or standard video." },
    { id: "instagram", label: "Instagram", href: "https://www.instagram.com/", icon: Instagram, hint: "Create a Reel and choose the downloaded MP4." },
    { id: "facebook", label: "Facebook Reels", href: "https://www.facebook.com/reels/create", icon: Facebook, hint: "Create a Reel for your profile or Page." },
    { id: "twitch", label: "Twitch Video Producer", href: "https://dashboard.twitch.tv/content/video-producer", icon: Twitch, hint: "Create a Twitch-native clip from the matching stream or VOD." },
];

function buildCaption(asset) {
    const title = asset?.title?.trim() || "WhiteWater AI highlight";
    const game = asset?.game && asset.game !== "reel" ? ` #${String(asset.game).replace(/\s+/g, "")}` : "";
    return `${title}\n\nCaptured with WhiteWater AI.${game} #gaming #gamingclips #highlights`;
}

export function SocialShareDialog({ asset, open, onOpenChange }) {
    const [shareUrl, setShareUrl] = useState("");
    const [busy, setBusy] = useState("");
    const mediaUrl = fileUrl(asset?.video_media_url);
    const caption = buildCaption(asset);

    useEffect(() => { if (!open) { setShareUrl(""); setBusy(""); } }, [open, asset?.id]);

    const ensureShareUrl = async () => {
        if (shareUrl) return shareUrl;
        const { data } = await api.post(`/clips/${asset.id}/share`);
        const url = `${window.location.origin}${data.path}`;
        setShareUrl(url);
        return url;
    };

    const copyText = async (value, success) => {
        try { await navigator.clipboard.writeText(value); toast.success(success); }
        catch { toast.error("Clipboard access was blocked. Select and copy the text manually."); }
    };

    const copyLink = async () => {
        setBusy("link");
        try { await copyText(await ensureShareUrl(), "Public clip link copied."); }
        catch { toast.error("Could not create the public clip link."); }
        finally { setBusy(""); }
    };

    const downloadVideo = async () => {
        if (!mediaUrl) return;
        setBusy("download");
        try {
            const response = await fetch(mediaUrl, { credentials: "include" });
            if (!response.ok) throw new Error("media download failed");
            const objectUrl = URL.createObjectURL(await response.blob());
            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = `${(asset.title || "whitewater-clip").replace(/[^a-z0-9_-]+/gi, "-")}.mp4`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
            toast.success("MP4 downloaded and ready to upload.");
        } catch { toast.error("Could not download this video."); }
        finally { setBusy(""); }
    };

    const nativeShare = async () => {
        if (!navigator.share) { toast.error("Use Download MP4 on this browser."); return; }
        setBusy("share");
        try {
            if (mediaUrl) {
                const response = await fetch(mediaUrl, { credentials: "include" });
                if (!response.ok) throw new Error("media download failed");
                const blob = await response.blob();
                const file = new File([blob], `${(asset.title || "whitewater-clip").replace(/[^a-z0-9_-]+/gi, "-")}.mp4`, { type: "video/mp4" });
                if (navigator.canShare?.({ files: [file] })) {
                    await navigator.share({ files: [file], title: asset.title, text: caption });
                    return;
                }
            }
            const url = await ensureShareUrl();
            await navigator.share({ title: asset.title, text: caption, url });
        } catch (error) {
            if (error?.name !== "AbortError") toast.error("Sharing was unavailable. Download the MP4 instead.");
        } finally { setBusy(""); }
    };

    const openPlatform = (platform) => {
        navigator.clipboard?.writeText(caption).catch(() => {});
        window.open(platform.href, "_blank", "noopener,noreferrer");
        toast.success("Caption copied. Upload the downloaded MP4 in the new tab.");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-surface border-border max-w-xl max-h-[90vh] overflow-y-auto" data-testid="social-share-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Publish or share</DialogTitle>
                    <DialogDescription>Prepare “{asset?.title || "this clip"}” for social media without losing the original.</DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <button onClick={downloadVideo} disabled={!mediaUrl || !!busy} data-testid="social-download"
                        className="h-10 rounded-sm bg-volt text-black font-semibold flex items-center justify-center gap-2 hover:bg-[#33ceff] disabled:opacity-50">
                        {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download MP4
                    </button>
                    <button onClick={nativeShare} disabled={!mediaUrl || !!busy} data-testid="social-native-share"
                        className="h-10 rounded-sm border border-volt/50 text-volt font-semibold flex items-center justify-center gap-2 hover:bg-volt/10 disabled:opacity-50">
                        {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share video
                    </button>
                    <button onClick={copyLink} disabled={!asset?.id || !!busy} data-testid="social-copy-link"
                        className="h-10 rounded-sm border border-border text-white font-semibold flex items-center justify-center gap-2 hover:border-white/40 disabled:opacity-50">
                        {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Copy link
                    </button>
                </div>

                <div className="rounded-sm border border-border bg-obsidian/50 p-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Ready-to-post caption</span>
                        <button onClick={() => copyText(caption, "Caption copied.")} data-testid="social-copy-caption" className="text-volt text-xs flex items-center gap-1 hover:underline">
                            <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                    </div>
                    <p className="text-xs text-foreground/90 whitespace-pre-wrap">{caption}</p>
                </div>

                <div className="space-y-2">
                    {PLATFORMS.map((platform) => {
                        const Icon = platform.icon;
                        return (
                            <button key={platform.id} onClick={() => openPlatform(platform)} data-testid={`social-open-${platform.id}`}
                                className="w-full min-h-12 rounded-sm border border-border bg-obsidian/30 px-3 py-2 flex items-center gap-3 text-left hover:border-volt/50 transition-colors">
                                <Icon className="w-5 h-5 text-volt shrink-0" />
                                <span className="min-w-0 flex-1"><span className="block text-sm text-white font-semibold">{platform.label}</span><span className="block text-[11px] text-muted-foreground">{platform.hint}</span></span>
                                <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
                            </button>
                        );
                    })}
                </div>

                <p className="text-[11px] text-muted-foreground font-mono">9:16 exports are optimized for Shorts and Reels. Twitch clips must originate from a Twitch stream or VOD.</p>
            </DialogContent>
        </Dialog>
    );
}
